import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * TenseShift — a one-tap time machine for a row of sentences.
 *
 * Each row holds a sentence the student already says (`from_*`), with its
 * verb highlighted violet, and a time-word button. Tapping it travels the
 * sentence to the other tense (`to_*`): the verb is redrawn with its new
 * marking highlighted, the note explains the swap, and the audio plays.
 * Tapping again travels back for comparison. Completes when every row has
 * been shifted once.
 *
 * The marking is data-driven, so the same machine runs both directions:
 * lesson 18 travels to yesterday and highlights a `to_suffix` tail, while
 * lesson 20 travels to tomorrow and highlights the `to_prefix` سَـ. Items
 * with neither (lesson 18's bare هُوَ form) render the verb fully in gold
 * with nothing marked violet — there is nothing extra to point at.
 */
function FromSentence({ item }) {
  const { from_ar: text, from_verb: verb } = item;
  const idx = text.indexOf(verb);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color: "var(--tag-violet)" }}>{verb}</span>
      {text.slice(idx + verb.length)}
    </>
  );
}

function ToSentence({ item }) {
  const { to_ar: text, to_verb: verb, to_prefix: prefix, to_suffix: suffix } = item;
  const idx = text.indexOf(verb);
  if (idx === -1) return <>{text}</>;
  const head = prefix && verb.startsWith(prefix) ? prefix : "";
  const tail = suffix && verb.endsWith(suffix) ? suffix : "";
  const stem = verb.slice(head.length, verb.length - tail.length);
  return (
    <>
      {text.slice(0, idx)}
      {head && <span style={{ color: "var(--tag-violet)" }}>{head}</span>}
      <span style={{ color: "var(--gold-bright)" }}>{stem}</span>
      {tail && <span style={{ color: "var(--tag-violet)" }}>{tail}</span>}
      {text.slice(idx + verb.length)}
    </>
  );
}

export default function TenseShift({ data, onComplete, audio }) {
  const [shifted, setShifted] = useState(() => new Set());
  const [everShifted, setEverShifted] = useState(() => new Set());

  const done = everShifted.size >= data.items.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleShift = (item) => {
    const isShifted = shifted.has(item.id);
    setShifted((prev) => {
      const next = new Set(prev);
      if (isShifted) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
    if (!isShifted) {
      setEverShifted((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
      if (audio) audio.play(item.to_ar);
    }
  };

  return (
    <ActivityCard>
      <div className="flex justify-end mb-3">
        <ProgressPill>{everShifted.size}/{data.items.length} travelled</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {data.items.map((item) => {
          const isShifted = shifted.has(item.id);
          const seen = everShifted.has(item.id);
          return (
            <div
              key={item.id}
              className="px-4 py-3.5 rounded-xl"
              style={{
                background: isShifted ? "var(--gold-bg)" : "var(--ink)",
                border: seen ? "1px solid var(--gold-dim)" : "1px solid var(--panel-line)",
              }}
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleShift(item)}
                  aria-pressed={isShifted}
                  title={isShifted ? data.back_label : data.shift_title}
                  className="shrink-0 self-center rounded-lg px-3 py-2 transition-all hover:brightness-110"
                  style={
                    isShifted
                      ? { background: "var(--gold)", border: "1px solid var(--gold)", color: "var(--ink)" }
                      : { background: "var(--ink)", border: "1px solid var(--gold-dim)", color: "var(--gold-bright)" }
                  }
                >
                  <span lang="ar" dir="rtl" className="font-arabic text-2xl leading-[1.4] block">
                    {data.shift_label}
                  </span>
                  <span className="text-[10px] block" style={isShifted ? undefined : { color: "var(--parchment-dim)" }}>
                    {isShifted ? data.back_label : data.shift_label_en}
                  </span>
                </button>

                <div className="flex-1 min-w-0">
                  <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.7] m-0" style={{ color: "var(--parchment)" }}>
                    {isShifted ? <ToSentence item={item} /> : <FromSentence item={item} />}
                  </p>
                  <p className="text-sm mt-0.5 mb-0" style={{ color: "var(--parchment-dim)" }}>
                    {isShifted ? item.to_en : item.from_en}
                  </p>
                  {isShifted && item.note && (
                    <p className="text-sm mt-1.5 mb-0 px-3 py-1.5 rounded-lg inline-block" style={{ background: "var(--violet-bg)", color: "var(--parchment)" }}>
                      <MixedText text={item.note} />
                    </p>
                  )}
                </div>

                {audio && (
                  <PlayButton
                    text={isShifted ? item.to_ar : item.from_ar}
                    playingText={audio.playingText}
                    onPlay={audio.play}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">{data.icon || "⏪"}</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.done_message} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
